/**
 * customize-checkout.js — Checkout interception for the cart customization popup (Ticket D)
 *
 * Intercepts checkout form submits on:
 *   - Cart page:   <form class="cart-form …">      (sections/main-cart.liquid)
 *   - Cart drawer: <form class="buy-buttons …">    (sections/cart-drawer.liquid)
 *
 * When >=1 eligible-not-customised line exists, prevents the submit and opens the
 * advisory popup. The popup is advisory only: dismissing it always lets checkout
 * proceed. This code must NEVER be able to permanently prevent a shopper from
 * checking out — it fails open on every error path.
 *
 * ── Listener strategy: delegated on document (bubble phase) ──────────────────
 *
 * We attach ONE `submit` listener to `document` rather than to the forms
 * themselves, for two distinct reasons that both matter:
 *
 * 1. Survives however the cart happens to refresh. The two surfaces differ:
 *    the cart DRAWER re-renders in place via the Section Rendering API on a
 *    quantity change, replacing the form element; the cart PAGE instead does a
 *    full navigation (assets/theme.js.liquid `_changeLineItemQuantity` sets
 *    window.location.href when pageType === "cart"). A listener bound directly
 *    to a form element would be silently lost in the drawer case. A
 *    document-level listener is robust to both, because `document` is never
 *    replaced and a full reload simply re-runs this script.
 *
 * 2. Guarantees correct ordering with the charity gate, by construction. The
 *    charity handler (sections/main-cart.liquid ~line 1104-1127) is bound
 *    directly to `.cart-form` in a DOMContentLoaded callback. The submit event
 *    bubbles from the form element up to `document`, so the charity handler
 *    fires FIRST. By the time our handler runs, `event.defaultPrevented` is
 *    already true if the charity gate blocked the submit. We rely on this flag
 *    rather than script-load timing.
 *
 * We use the bubble phase (not capture) to ensure this ordering is guaranteed
 * by the DOM event model rather than by accident.
 *
 * ── Decision sequence (fail open at each step) ───────────────────────────────
 *
 *  1. Bail unless event.target is an HTMLFormElement.
 *  2. Bail unless the form is .cart-form or .buy-buttons (the two checkout forms).
 *  3. Bail unless event.submitter.name === 'checkout'. Prevents intercepting
 *     cart-note saves, quantity updates, and express-checkout buttons (PayPal,
 *     Shop Pay) whose Shopify-rendered forms or submitters do not carry the
 *     'checkout' name. Fail open when event.submitter is falsy (old Safari or
 *     programmatic submit).
 *  4. Bail if event.defaultPrevented — the charity gate (or another handler)
 *     already blocked this submit. Charity precedence is guaranteed here.
 *  5. Bail if hasPrompted is already true — at-most-once per page session.
 *  6. Bail if the popup element is absent or eligibleCount === 0.
 *  7. Otherwise: set flag, preventDefault(), capture references, popup.show().
 *
 * ── hasPrompted — the once-per-session gate ───────────────────────────────────
 *
 * Set to true at step 7 and never reset. Any subsequent checkout click passes
 * through step 5 and proceeds to checkout normally. This satisfies both:
 *   - "at most once per page session"
 *   - "dismiss lets checkout proceed" (Escape / X / overlay → just close;
 *     next click passes step 5 and goes straight through)
 *
 * ── No button — immediate resume ─────────────────────────────────────────────
 *
 * When the shopper clicks No, assets/customize-popup.js dispatches a
 * 'cart-customize:decline' CustomEvent (bubbles: true) before calling hide().
 * We handle it here to immediately resume the intercepted submit so the shopper
 * does not have to click checkout a second time.
 *
 * Resume uses requestSubmit(submitter) so the submit button's name/value
 * ('checkout') is included in the POST payload — Shopify only routes
 * POST /cart to checkout when that parameter is present. A plain form.submit()
 * call would silently just update the cart instead.
 *
 * requestSubmit re-fires the submit event. That is safe and intentional:
 * hasPrompted is already true, so step 5 of our handler bails immediately and
 * the event propagates to Shopify's handlers unimpeded.
 *
 * Safari < 15.4 fallback: requestSubmit is not available. Feature-detect with
 * `typeof form.requestSubmit === 'function'` and fall back to appending a
 * temporary hidden <input> carrying the submitter's name/value, then form.submit().
 *
 * ── Files changed ─────────────────────────────────────────────────────────────
 *  assets/customize-checkout.js   — this file (new)
 *  assets/customize-popup.js      — added 'cart-customize:decline' dispatch in No handler;
 *                                   updated API documentation header
 *  snippets/customize-cart-popup.liquid — added <script> tag to load this file
 *
 * ── Files intentionally NOT changed ──────────────────────────────────────────
 *  sections/main-cart.liquid      — no touch needed; delegated listener design
 *  sections/cart-drawer.liquid    — no touch needed; delegated listener design
 *  layout/theme.liquid            — loaded transitively via customize-cart-popup.liquid
 *
 * ── No dependencies, no imports, plain ES2020 ─────────────────────────────────
 */

(function () {
  'use strict';

  // ── Module-scoped state ────────────────────────────────────────────────────

  /**
   * Set to true the first time we intercept a checkout submit.
   * Never reset — subsequent clicks always pass through.
   * @type {boolean}
   */
  var hasPrompted = false;

  /**
   * The form and submitter captured at interception time.
   * Held so the decline handler can call requestSubmit without the shopper
   * clicking checkout a second time.
   * @type {HTMLFormElement|null}
   */
  var _pendingForm = null;

  /**
   * @type {HTMLElement|null}
   */
  var _pendingSubmitter = null;

  // ── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Returns true when the form is one of the two checkout surfaces.
   * Explicit class checks prevent matching every form on the page
   * (search forms, newsletter forms, etc.).
   *
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  function isCheckoutForm(form) {
    return form.classList.contains('cart-form') ||
           form.classList.contains('buy-buttons');
  }

  /**
   * Resume the intercepted checkout submit after the No button is clicked.
   *
   * Uses requestSubmit(submitter) so the submit button's name/value is included
   * in the POST body — Shopify routes POST /cart to checkout only when the
   * 'checkout' parameter is present. form.submit() would omit it.
   *
   * requestSubmit re-fires the submit event. That is intentional: hasPrompted
   * is already true at this point, so our submit handler bails at step 5 and
   * the event propagates normally to Shopify's handlers.
   *
   * Safari < 15.4 fallback: requestSubmit is absent. Append a temporary hidden
   * <input> carrying the submitter's name/value, then call form.submit().
   *
   * @param {HTMLFormElement} form
   * @param {HTMLElement|null} submitter
   */
  function resumeCheckout(form, submitter) {
    if (!form) return;

    try {
      if (typeof form.requestSubmit === 'function') {
        // Pass the original submitter so 'checkout' appears in the POST body.
        // undefined (not null) means "use the form's default submit button".
        form.requestSubmit(submitter || undefined);
      } else {
        // Safari < 15.4 fallback.
        // Inject a temporary hidden input so 'checkout' is in the POST body.
        var tempInput = null;
        if (submitter && submitter.name) {
          tempInput = document.createElement('input');
          tempInput.type  = 'hidden';
          tempInput.name  = submitter.name;
          tempInput.value = submitter.value || '';
          form.appendChild(tempInput);
        }
        form.submit();
        // Remove the temporary input. May be a no-op if the page navigates
        // away immediately, which is the expected case.
        if (tempInput && tempInput.parentNode === form) {
          form.removeChild(tempInput);
        }
      }
    } catch (err) {
      // If resume itself fails, let the error surface for debugging.
      // The shopper can still click checkout again (hasPrompted is set → step 5
      // bails → checkout proceeds).
      console.warn('[customize-checkout] resumeCheckout error:', err);
    }
  }

  // ── Submit interception ────────────────────────────────────────────────────

  document.addEventListener('submit', function (event) {
    // Wrap the entire body so any unhandled error fails open.
    // If we have already called event.preventDefault() and then something
    // throws, the catch block resumes checkout so the shopper is not stuck.
    var capturedForm      = null;
    var capturedSubmitter = null;

    try {

      // Step 1: Bail unless the target is a form.
      if (!(event.target instanceof HTMLFormElement)) return;
      var form = event.target;

      // Step 2: Bail unless this is one of the two checkout surfaces.
      if (!isCheckoutForm(form)) return;

      // Step 3: Bail unless the submitter is the checkout button.
      //
      // This is essential for two reasons:
      //   a) .cart-form contains cart-note and quantity inputs that also submit
      //      the form — intercepting those would silently break cart updates.
      //   b) {{ content_for_additional_checkout_buttons }} renders PayPal / Shop
      //      Pay buttons. Even if they share a form, their submitter would not
      //      carry name="checkout".
      //
      // Fail open when event.submitter is falsy: programmatic form.submit()
      // sets it to null; browsers without SubmitEvent.submitter (Safari < 15.4)
      // leave the property undefined. In either case, let the submit through.
      var submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;

      // Step 4: Bail if a prior handler already prevented the default.
      // The charity gate (sections/main-cart.liquid ~1104-1127) attaches its
      // handler directly to .cart-form, so it runs before this document-level
      // handler. If charity blocked the submit, event.defaultPrevented is true
      // here. We must not show the popup on top of the charity validation.
      if (event.defaultPrevented) return;

      // Step 5: Bail if we already prompted this session.
      // Once set, hasPrompted is never cleared; any subsequent checkout click
      // passes straight through to Shopify.
      if (hasPrompted) return;

      // Step 6: Bail if the popup is absent or has no eligible items.
      // popup is null when settings.customize_enabled is off (the element is not
      // rendered at all). !popup.eligibleCount handles both 0 and undefined
      // (if the custom element is not yet upgraded).
      var popup = document.getElementById('cart-customize-popup');
      if (!popup || !popup.eligibleCount) return;

      // Step 7: Intercept. Capture references before show() in case it throws.
      event.preventDefault();
      hasPrompted       = true;
      capturedForm      = form;
      capturedSubmitter = submitter;

      // Clear the checkout button's loading state.
      //
      // The checkout button is `is="custom-button"`. CustomButton (assets/theme.js.liquid
      // ~line 416) binds a submit listener to its own form in its constructor that
      // sets aria-busy="true", which swaps the button label for an infinitely
      // repeating loader animation. It is only ever cleared on `pageshow` — i.e. on
      // the assumption that the submit always navigates away.
      //
      // We just cancelled that navigation, so without this the checkout button would
      // spin forever: while the popup is open, and still afterwards if the shopper
      // dismisses it. That reads as a broken checkout button.
      //
      // Its listener is bound to the form, so it has already run by the time this
      // document-level handler fires; removing the attribute here triggers
      // CustomButton's attributeChangedCallback, which animates the label back.
      if (submitter.getAttribute('aria-busy') === 'true') {
        submitter.removeAttribute('aria-busy');
      }

      // Mirror into module state for the decline handler.
      _pendingForm      = form;
      _pendingSubmitter = submitter;

      popup.show();

    } catch (err) {
      // Fail open. If we already called preventDefault(), resume checkout now
      // so the shopper is not blocked.
      if (capturedForm) {
        _pendingForm      = null;
        _pendingSubmitter = null;
        resumeCheckout(capturedForm, capturedSubmitter);
      }
      console.warn('[customize-checkout] submit handler error:', err);
    }
  });

  // ── Decline handler ────────────────────────────────────────────────────────

  /**
   * Handles the 'cart-customize:decline' CustomEvent dispatched by the popup's
   * No button (assets/customize-popup.js). Resumes the intercepted checkout
   * submit immediately so the shopper does not need to click checkout again.
   *
   * Re-entry safety: requestSubmit re-fires the submit event, but hasPrompted
   * is already true at this point, so our submit handler bails at step 5 and
   * the event propagates normally to Shopify. No double-fire, no deadlock.
   */
  document.addEventListener('cart-customize:decline', function () {
    try {
      // Snapshot and clear module state before resuming to prevent any
      // possibility of re-entrant resume if decline fires more than once.
      var form      = _pendingForm;
      var submitter = _pendingSubmitter;
      _pendingForm      = null;
      _pendingSubmitter = null;

      // Guard: bail gracefully if nothing was pending (should not occur in
      // normal operation, but be defensive).
      if (!form) return;

      resumeCheckout(form, submitter);

    } catch (err) {
      console.warn('[customize-checkout] decline handler error:', err);
    }
  });

}());
